import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Typography,
  Button,
  Tag,
  Spin,
  Tabs,
} from 'antd';
import { fetchOrders } from '../../store/slices/ordersSlice';
import ErrorMessage from '../common/ErrorMessage';

const { Title, Text } = Typography;

const PendingOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orders, pagination, loading, error } = useSelector((state) => state.orders);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState('pending');

  useEffect(() => {
    dispatch(
      fetchOrders({
        page: page,
        limit: pageSize,
        status: statusFilter === 'all' ? undefined : statusFilter,
      })
    );
  }, [dispatch, page, pageSize, statusFilter]);

  const handleTableChange = (pagination) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleStatusChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'blue';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading && !orders.length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      render: (id) => `#${id}`,
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => (
        record.User ? `${record.User.first_name} ${record.User.last_name}` : 'N/A'
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phone) => phone || 'N/A',
    },
    {
      title: 'Address',
      dataIndex: 'delivery_address',
      key: 'delivery_address',
      render: (address) => <Text style={{ maxWidth: 200 }} ellipsis>{address}</Text>,
    },
    {
      title: 'Items',
      key: 'items',
      render: (_, record) => record.OrderItems ? record.OrderItems.length : 0,
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `${(amount || 0).toLocaleString()} UZS`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ textTransform: 'capitalize' }}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          onClick={() => navigate(`/seller/orders/${record.id}`)}
        >
          Manage
        </Button>
      ),
      align: 'right',
    },
  ];

  const items = [
    { label: 'Pending', key: 'pending' },
    { label: 'Confirmed', key: 'confirmed' },
    { label: 'Completed', key: 'completed' },
    { label: 'Cancelled', key: 'cancelled' },
    { label: 'All', key: 'all' },
  ];

  return (
    <div>
      <Title level={2} style={{ marginBottom: 24 }}>
        Order Management
      </Title>

      {error && <ErrorMessage message={error} />}

      {/* Status Tabs */}
      <Tabs
        activeKey={statusFilter}
        onChange={handleStatusChange}
        items={items}
        style={{ marginBottom: 24 }}
      />

      {/* Orders Table */}
      <Table
        columns={columns}
        dataSource={orders}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: pageSize,
          total: pagination?.total || 0,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        scroll={{ x: true }}
      />
    </div>
  );
};

export default PendingOrders;
